import { useRef, useState } from 'react';
import { Button, Input, Space, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { orgApi } from '../api';
import type { OrgBrandingAssetKind } from '../types';

/**
 * One branding image of an organization: URL, Upload button, preview (AD-141).
 *
 * Uploading stores the file in the public bucket and fills the field with its
 * URL. Nothing is saved on the org until the Branding section's own Save, so the
 * preview can be checked before the client's site changes. Pasting an https URL
 * still works.
 */
export default function BrandingImageInput({
  orgId,
  kind,
  value,
  onChange,
}: {
  orgId: number;
  kind: OrgBrandingAssetKind;
  value: string | null;
  onChange: (value: string | null) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function onFile(file: File | undefined) {
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await orgApi.uploadBrandingAsset(orgId, kind, file);
      onChange(url);
      message.success('Uploaded. Save branding to apply it.');
    } catch {
      // The API client already toasts the backend's message.
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  const invalid = !!value && !value.startsWith('https://');

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Space.Compact style={{ width: '100%' }}>
        <Input
          allowClear
          value={value ?? ''}
          status={invalid ? 'error' : undefined}
          onChange={(event) => onChange(event.target.value || null)}
          placeholder="None: Seiki's own"
        />
        <Button
          icon={<UploadOutlined />}
          loading={uploading}
          onClick={() => inputRef.current?.click()}
        >
          Upload
        </Button>
      </Space.Compact>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/x-icon,.ico"
        style={{ display: 'none' }}
        onChange={(event) => onFile(event.target.files?.[0])}
      />
      {value && !invalid && (
        <img
          src={value}
          alt=""
          style={{
            maxHeight: 40,
            maxWidth: 220,
            padding: 4,
            border: '1px solid #e5e7eb',
            borderRadius: 4,
          }}
        />
      )}
    </Space>
  );
}
